import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@packages/api';
import { getLedgerStatus } from '@packages/api/endpoints/ledger.js';
import { useAuth } from '../lib/auth/useAuth.js';

/**
 * Returns whether the active account has a live business ledger and/or a live
 * trust ledger, plus the loading and error state of the underlying query.
 *
 * Gate: member-facing features that depend on `hasActiveLedger` /
 * `hasActiveTrustLedger` (server-computed, member-readable booleans served by
 * `GET /accounts/:accountId/ledger-status`). Distinct from the super-admin
 * gate and the `useAccountingAccess()` accounting-flag gate.
 *
 * `routes/client-statement/index.tsx` (loading/error + gate on
 * `hasActiveLedger` alone - trust-side presence comes from the statement
 * feed's per-ledger slice status, not this hook) and
 * `routes/hub/HubHeaderActions.tsx` (show/hide the "Client statement" button)
 * use this hook rather than reading account state directly.
 *
 * Loading semantics: while `accountId` is absent (auth still initialising) the
 * query is disabled and `isPending` is true, so consumers see `isLoading:true`
 * rather than a false-negative `hasActiveLedger:false`. This prevents the
 * "Statements not available" empty state from flashing during a hard refresh.
 */
export function useHasActiveLedger(): {
  hasActiveLedger: boolean;
  hasActiveTrustLedger: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { accountId } = useAuth();

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.ledgerStatus(accountId ?? ''),
    queryFn: () => getLedgerStatus(accountId!),
    enabled: !!accountId,
  });

  return {
    hasActiveLedger: data?.hasActiveLedger === true,
    // `=== true` mirrors hasActiveLedger above rather than trusting `data`
    // truthiness alone - the query is disabled/pending case is covered by
    // isLoading below.
    hasActiveTrustLedger: data?.hasActiveTrustLedger === true,
    // While accountId is absent the query is disabled and stays in `pending`
    // state. Treat that as loading so consumers never flash the empty-state
    // before auth has settled.
    isLoading: isPending,
    isError,
    refetch: () => void refetch(),
  };
}
